import React, { useMemo, useState } from 'react';
import { Badge, Icon, Paper, Tab, Tabs } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import OrganisatiesActionMenu from './OrganisatiesActionMenu';
import { PortfolioOrganization, PortfolioPlan } from '../../../models/portfolio/portfolio';

import { authService } from '../../../services/auth.service';
import { contentService } from '../../../services/content.service';
import { pageSizeStorageKey } from '../../../services/utils/localStorageKeys';
import { usePersistedPaginationModel } from '../../../hooks/usePersistedPageSize';


interface OverzichtOrganisatiesProps {
  organisaties?: PortfolioOrganization[];
  portfolioId: string;
  mijnOrganisatieId?: string;
  canCrud?: boolean;
  plannen?: PortfolioPlan[];
}

type OrganisatieTabValue = 0 | 1;

const ACTIEVE_STATUSSEN = ['pending', 'accepted'];
const LS_PAGE_SIZE_KEY_ACTIEF = pageSizeStorageKey('portfolio.organisaties.actief');
const LS_PAGE_SIZE_KEY_ARCHIEF = pageSizeStorageKey('portfolio.organisaties.archief');

const OverzichtOrganisaties: React.FC<OverzichtOrganisatiesProps> = ({
  organisaties,
  portfolioId,
  mijnOrganisatieId,
  canCrud,
  plannen,
}) => {
  const [tabValue, setTabValue] = useState<OrganisatieTabValue>(0);
  const hasCrudRights = canCrud ?? authService.canCrud();
  const currentPageSizeKey = tabValue === 0 ? LS_PAGE_SIZE_KEY_ACTIEF : LS_PAGE_SIZE_KEY_ARCHIEF;
  const { paginationModel, handlePaginationModelChange } = usePersistedPaginationModel(currentPageSizeKey);

  const eigenOrganisatieActief = useMemo(() => {
    return (organisaties ?? []).some(
      (organisatie) => organisatie.organizationId === mijnOrganisatieId && (organisatie.status ?? '').toLowerCase() === 'accepted'
    );
  }, [organisaties, mijnOrganisatieId]);

  const actieveOrganisaties = useMemo(() => {
    return (organisaties ?? []).filter((organisatie) =>
      ACTIEVE_STATUSSEN.includes((organisatie.status ?? '').toLowerCase())
    );
  }, [organisaties]);

  const inactieveOrganisaties = useMemo(() => {
    return (organisaties ?? []).filter((organisatie) =>
      !ACTIEVE_STATUSSEN.includes((organisatie.status ?? '').toLowerCase())
    );
  }, [organisaties]);

  const rows = tabValue === 0 ? actieveOrganisaties : inactieveOrganisaties;

  const getStatusLabel = (status?: string) => {
    const label = contentService.getInclusionRequestStatusLabel(status ?? '');
    return label ?? status ?? '';
  };

  const formatStatusChangedOn = (value?: string | null) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const gridColumns = useMemo<(GridColDef<PortfolioOrganization> | GridActionsColDef<PortfolioOrganization>)[]>(() => {
    const columns: (GridColDef<PortfolioOrganization> | GridActionsColDef<PortfolioOrganization>)[] = [
      {
        field: 'displayName',
        headerName: 'Organisatie',
        flex: 2,
        renderCell: (params: GridRenderCellParams<PortfolioOrganization>) => (
          <>{params.row.displayName || params.row.organizationId}</>
        ),
      },
      {
        field: 'status',
        headerName: 'Deelname',
        flex: 1,
        renderCell: (params: GridRenderCellParams<PortfolioOrganization>) => (
          <>{getStatusLabel(params.row.status)}</>
        ),
      },
      {
        field: 'actions',
        headerName: 'Functies',
        renderHeader: () => <></>,
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<PortfolioOrganization>) => (
          <OrganisatiesActionMenu
            params={params}
            portfolioId={portfolioId}
            mijnOrganisatieId={mijnOrganisatieId}
            eigenOrganisatieActief={eigenOrganisatieActief}
            canCrud={hasCrudRights}
            plannen={plannen}
          />
        ),
      },
    ];

    if (tabValue === 1) {
      columns.splice(2, 0, {
        field: 'statusChangedOn',
        headerName: 'Status laatst gewijzigd',
        flex: 1,
        sortable: true,
        valueGetter: (_value, row) => row.statusChangedOn ?? null,
        renderCell: (params: GridRenderCellParams<PortfolioOrganization>) => (
          <>{formatStatusChangedOn(params.row.statusChangedOn)}</>
        ),
      });
    }

    return columns;
  }, [eigenOrganisatieActief, hasCrudRights, mijnOrganisatieId, portfolioId, tabValue, plannen]);

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={(_e, value: OrganisatieTabValue) => setTabValue(value)}
        aria-label="organisaties tabs"
        TabIndicatorProps={{ className: `indicator-${tabValue}` }}
        sx={{ overflow: 'visible', '& .MuiTabs-flexContainer': { overflow: 'visible' } }}
      >
        <Tab
          value={0}
          label={(
            <Badge badgeContent={actieveOrganisaties.length} color="primary" overlap="rectangular" sx={{ '& .MuiBadge-badge': { right: -14, top: 2 } }}>
              <span>Organisaties</span>
            </Badge>
          )}
          icon={<Icon className={`icon-cirle pending`}>business</Icon>}
          iconPosition="start"
          className="pending"
          sx={{ overflow: 'visible' }}
        />
        <Tab
          value={1}
          label={(
            <Badge badgeContent={inactieveOrganisaties.length} color="primary" overlap="rectangular" sx={{ '& .MuiBadge-badge': { right: -14, top: 2 } }}>
              <span>Archief</span>
            </Badge>
          )}
          icon={<Icon className={`icon-cirle ongoing`}>archive</Icon>}
          iconPosition="start"
          className="ongoing"
          sx={{ overflow: 'visible' }}
        />
      </Tabs>

      <div className="grid-responsive">
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={gridColumns}
            getRowId={(row) => row.organizationId}
            autoHeight
            localeText={{
              noRowsLabel: 'Geen organisaties',
              MuiTablePagination: {
                labelRowsPerPage: 'Organisaties per pagina:',
                labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) => `${from}-${to} van ${count}`,
              },
            }}
            disableColumnFilter
            disableColumnMenu
            disableRowSelectionOnClick
            sx={{ border: 0 }}
            pagination
            pageSizeOptions={[5, 10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            initialState={{
              sorting: {
                sortModel: [{ field: 'displayName', sort: 'asc' }],
              },
            }}
          />
        </Paper>
      </div>
    </>
  );
};

export default OverzichtOrganisaties;
